import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { displayToast } from "../../utils/displayToast";
import { Eye, EyeOff, Factory, BarChart3, Settings, Package, TrendingUp, ShieldCheck, AlertTriangle, ArrowRight } from "lucide-react";
import { login } from "../../apis/auth";
import "../../styles/auth/Login.css";

const Login = ({ onLogin }) => {
  const navigate = useNavigate();

  const [user_name, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");

  const validateForm = () => {
    if (user_name.trim() === "") {
      setLoginError("Username is required");
      return false;
    }

    if (password.trim() === "") {
      setLoginError("Please enter a password");
      return false;
    }

    if (password.trim().length < 8) {
      setLoginError("Password must be at least 8 characters");
      return false;
    }

    setLoginError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    const loginData = {
      user_name,
      password,
    };
    try {
      const data = await login(loginData);
      if (data.description) {
        displayToast(data.description, "error");
      } else if (data.token) {
        const organisationEmail = data?.organisation?.email;
        onLogin(organisationEmail);
        localStorage.setItem("auth_token", data.token);
        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-wrapper">
        <div className="login-branding">
          <div className="branding-content">
            <div className="brand-logo" aria-hidden="true"><Factory size={48} strokeWidth={1.5} /></div>
            <h1 className="brand-title">Organisation Admin</h1>
            <p className="brand-subtitle">Enterprise Resource Planning</p>
            <div className="brand-features">
              <div className="feature-item">
                <span className="feature-icon"><BarChart3 size={20} /></span>
                <span>Real-time Analytics</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"><Settings size={20} /></span>
                <span>Equipment Monitoring</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"><Package size={20} /></span>
                <span>Inventory Control</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"><TrendingUp size={20} /></span>
                <span>Production Insights</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="login-form-section">
          <div className="login-card">
            <div className="login-header">
              <h2>Welcome Back</h2>
              <p>Sign in to access your dashboard</p>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Username</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    placeholder="Enter your username"
                    value={user_name}
                    onChange={(e) => setUsername(e.target.value)}
                   className={loginError && !user_name.trim() ? "input-error" : ""}
                   aria-invalid={Boolean(loginError && !user_name.trim())}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Password</label>
                <div className="password-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                   className={loginError && !password.trim() ? "input-error" : ""}
                   aria-invalid={Boolean(loginError && !password.trim())}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword((prev) => !prev)}
                   aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <Eye size={18} color="#64748b" />
                    ) : (
                      <EyeOff size={18} color="#64748b" />
                    )}
                  </button>
                </div>
              </div>
              
              <div className="login-error-message-container">
                {loginError && (
                  <div className="login-error-message">
                    <AlertTriangle size={16} className="error-icon" />
                    {loginError}
                  </div>
                )}
              </div>
              
              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? (
                  <span className="loader"></span>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight size={18} className="button-arrow" />
                  </>
                )}
              </button>
            </form>
            
            <div className="login-footer">
              <p className="security-notice">
                <ShieldCheck size={14} /> Secure enterprise-grade authentication
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
